import React from "react";
import type { NoteName } from "../../lib/guitarEngine";
import type { ChordDefinition, InversionType, ExplorerFilter } from "../../lib/chordEngine";
import type { PracticeSurface } from "../../lib/practiceSurface";
import type { buildChordToneMidis } from "../../lib/chordStaffEvents";
import { degreeColor } from "../../lib/practiceVisualTheme";
import {
  CHORD_CATEGORY_LABEL_ES,
  formatChordIntervalFormula,
  INVERSION_HINT_ES,
  INVERSION_LABEL_ES,
  VOICING_TYPE_LABEL_ES,
} from "../../lib/chordPedagogy";

type ChordTone = ReturnType<typeof buildChordToneMidis>[number];

export interface ChordTheoryInspectorProps {
  root: NoteName;
  def: ChordDefinition;
  inversion: InversionType;
  voicingType: ExplorerFilter["voicingType"];
  tones: ChordTone[];
  surface: PracticeSurface;
}

function chordDegreeAccent(degree: string): string {
  const d = degree.replace("*", "");
  if (d === "R" || d === "1") return "var(--bordona-accent)";
  if (d === "3" || d === "b3" || d === "#3") return "#06B6D4";
  if (d === "5" || d === "b5" || d === "#5") return "#22C55E";
  if (d.includes("7")) return "#A78BFA";
  return degreeColor(d);
}

export const ChordTheoryInspector: React.FC<ChordTheoryInspectorProps> = ({
  root,
  def,
  inversion,
  voicingType,
  tones,
  surface,
}) => {
  const noteLine = tones.map((t) => t.step).join(" · ");
  const categoryLabel = CHORD_CATEGORY_LABEL_ES[def.category] ?? def.category;

  return (
    <section
      className="p-4 rounded-2xl bg-white/[0.07] border border-white/12 space-y-3"
      style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.12)" }}
    >
      <p className="text-[10px] font-sans font-semibold uppercase tracking-[0.14em] text-slate-500">
        Conformación del acorde
      </p>

      <div>
        <h2 className="text-2xl font-serif font-bold italic text-slate-100 leading-tight">
          {root} {def.label}
        </h2>
        <p className="mt-1 text-[11px] font-sans text-slate-400">
          {categoryLabel}
          {surface === "keyboard" ? " · Teclado" : " · Mástil"}
        </p>
      </div>

      <p className="text-[12px] font-sans text-slate-300 leading-relaxed">
        Este acorde se construye apilando{" "}
        <span className="text-slate-100 font-medium">{def.intervals.length} sonidos</span> sobre{" "}
        <span className="font-serif italic text-slate-100">{root}</span>. En partitura y teclado
        cada color indica el grado dentro del acorde (no la escala mayor).
      </p>

      <div className="rounded-xl bg-white/[0.05] border border-white/10 px-3 py-2.5 space-y-1">
        <p className="text-[9px] font-sans uppercase tracking-wider text-slate-500">
          Notas que lo forman
        </p>
        <p className="text-sm font-sans font-semibold text-slate-100 tracking-wide">{noteLine}</p>
        <p className="text-[10px] font-sans text-slate-500">
          Fórmula: {formatChordIntervalFormula(def.intervals)}
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {tones.map((t) => {
          const accent = chordDegreeAccent(t.degree);
          return (
            <div
              key={`${t.midi}-${t.degree}`}
              className="flex flex-col items-center min-w-[2.75rem] px-2 py-1.5 rounded-xl border bg-white/[0.04]"
              style={{ borderColor: `${accent}55` }}
            >
              <span
                className="text-[11px] font-serif font-semibold leading-none"
                style={{ color: accent }}
              >
                {t.degree}
              </span>
              <span className="text-[10px] font-sans text-slate-300 mt-1">{t.step}</span>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-2 text-[11px] font-sans">
        <div className="flex justify-between gap-2 text-slate-400">
          <span className="text-slate-500 shrink-0">Grados teóricos</span>
          <span className="text-slate-200 text-right">{def.degreeNames.join(" · ")}</span>
        </div>
        <div className="flex justify-between gap-2 text-slate-400">
          <span className="text-slate-500 shrink-0">Inversión</span>
          <span className="text-slate-200 text-right">{INVERSION_LABEL_ES[inversion]}</span>
        </div>
        {surface === "keyboard" && (
          <div className="flex justify-between gap-2 text-slate-400">
            <span className="text-slate-500 shrink-0">Disposición</span>
            <span className="text-slate-200 text-right">{VOICING_TYPE_LABEL_ES[voicingType]}</span>
          </div>
        )}
      </div>

      <p className="text-[11px] font-sans text-slate-500 leading-snug border-t border-white/10 pt-2">
        {INVERSION_HINT_ES[inversion]}
        {surface === "keyboard" && voicingType !== "all" && (
          <>
            {" "}
            Disposición «{VOICING_TYPE_LABEL_ES[voicingType]}»: cambia qué nota queda más grave sin
            alterar el acorde.
          </>
        )}
      </p>
    </section>
  );
};
